import type { ConsentRequest, RequestedScope } from './coding-agents-types';

export type ConsentState =
  | 'requested'
  | 'partly_beyond_rights'
  | 'beyond_rights'
  | 'refused'
  | 'consented'
  | 'revoked'
  | 'stale';

export type ScopeAvailability = 'asked' | 'already' | 'not_available';

export interface ConsentRow {
  id: string;
  label: string;
  description: string;
  family: string;
  availability: ScopeAvailability;
}

export interface ConsentView {
  state: ConsentState;
  rows: ConsentRow[];
  /** Whether Allow is offered; the server still checks rights when it is pressed. */
  canAllow: boolean;
}

// Knowledge first, then app access, then anything the page does not know.
const FAMILY_ORDER: Record<string, number> = { knowledge: 0, app: 1 };
const rank = (family: string) => FAMILY_ORDER[family] ?? 2;

const availability = (scope: RequestedScope, held: Set<string>): ScopeAvailability =>
  // Current rights come first: a scope the person holds but may no longer
  // grant is not shown as available.
  !scope.withinRights ? 'not_available' : held.has(scope.id) ? 'already' : 'asked';

/**
 * The complete requested access, grouped, with what the person can and cannot
 * grant. Rows come only from the request: nothing is added, hidden or merged.
 * An existing grant's scopes never count as newly asked for.
 */
export function consentView(request: ConsentRequest): ConsentView {
  const held = new Set(request.alreadyGranted);
  const rows = request.scopes
    .map((scope, index) => ({ scope, index }))
    .sort((a, b) => rank(a.scope.family) - rank(b.scope.family) || a.index - b.index)
    .map(({ scope }) => ({
      id: scope.id,
      label: scope.label,
      description: scope.description,
      family: scope.family,
      availability: availability(scope, held),
    }));
  // What a new consent can restate: every requested scope within current
  // rights, newly asked or already held. A fresh request for scopes the agent
  // already holds still needs the person's explicit consent to complete.
  const grantable = rows.filter((row) => row.availability !== 'not_available');
  const unavailable = rows.filter((row) => row.availability === 'not_available');
  let state: ConsentState;
  if (request.status !== 'requested') state = request.status;
  else if (unavailable.length > 0 && grantable.length === 0) state = 'beyond_rights';
  else if (unavailable.length > 0) state = 'partly_beyond_rights';
  else state = 'requested';
  return { state, rows, canAllow: request.status === 'requested' && grantable.length > 0 };
}
